import { Component, TemplateRef, computed, input, signal, ChangeDetectionStrategy } from '@angular/core';

/**
 * Renders a list capped at `limit` rows with a "Show more / Show less" toggle.
 * The row markup is supplied by the consumer as a template, so each list keeps its
 * own layout:
 *
 *   <app-collapsible-list [items]="devices" [itemTemplate]="deviceRow" />
 *   <ng-template #deviceRow let-device>...</ng-template>
 */
@Component({
  selector: 'app-collapsible-list',
  standalone: false,
  templateUrl: './collapsible-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./collapsible-list.component.scss'],
})
export class CollapsibleListComponent {
  readonly items = input<readonly unknown[] | null | undefined>([]);
  readonly limit = input(3);
  /** Row template; receives `{ $implicit: item, index }` as its context. */
  readonly itemTemplate = input.required<TemplateRef<unknown>>();

  protected readonly expanded = signal(false);

  protected readonly visibleItems = computed(() => {
    const items = this.items() ?? [];
    return this.expanded() ? items : items.slice(0, this.limit());
  });

  protected readonly hiddenCount = computed(() => Math.max(0, (this.items()?.length ?? 0) - this.limit()));
  protected readonly canToggle = computed(() => (this.items()?.length ?? 0) > this.limit());

  protected toggle(): void {
    this.expanded.update((expanded) => !expanded);
  }
}
